import React, { useEffect, useState } from "react";
import { Link, Route } from "polyrouter";
import { useTranslation } from "utilities/i18n";
import {
  PolyIcon,
  ToggleSwitchInput,
  HelpLink,
  FeatureFlagsOrEnabled,
  FeatureFlagEnabled
} from "elements";
import { DOCS } from "Constants";
import "./AdminMenu.scss";
import { useUserMeta } from "utilities/userMeta";
import { usePermissions } from "rbac";
import { THEME_DARK, THEME_LIGHT, THEME_DEFAULT } from "store/userMeta";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store";
import { trackAction } from "monitoring";
import { updateEnv } from "store/env";
import { useLogout } from "../../auth";
import { LensTheme } from "generated/graphql";
import { trackMenuSelection } from "monitoring/trackMenuSelection";

export interface AdminMenuProps {
  user: any;
}

export const AdminMenu: React.FC<AdminMenuProps> = ({ user }) => {
  const [open, setOpen] = useState(false);
  const self = React.useRef<HTMLSpanElement>(null);
  const { t } = useTranslation();
  const logout = useLogout();
  const [setUserMeta] = useUserMeta();
  const hasPermission = usePermissions();
  const theme = useSelector((state: State) => {
    return state.userMeta?.theme ?? THEME_DEFAULT;
  });
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });

  const dispatch = useDispatch();

  const closeMenu = () => {
    setOpen(false);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!self?.current?.contains(e.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClick, false);

    return () => {
      document.removeEventListener("mousedown", handleClick, false);
    };
  }, []);

  if (!user) {
    return null;
  }

  const setTheme = (newTheme: LensTheme) => {
    setUserMeta({ theme: newTheme });
    trackAction(`SwitchedTheme-${newTheme}`);
  };

  const dropdownClasses = [
    "menu vertical close-quickly pl-0 font-sm text-left",
    open && "open",
    debugUserRoles && open && "allowed"
  ]
    .filter(c => !!c)
    .join(" ");

  const hasUserListPermission = hasPermission("READ_USERS", "*");

  const hasClaritiTrialAdminPermission = hasPermission(
    "clariti-trial:ReadSubscriptions"
  );

  return (
    <span ref={self}>
      <nav>
        <Route
          render={({ location }) => (
            <span
              onClick={() => setOpen(!open)}
              className={
                "icon-wrap profile-icon" +
                (location.pathname.startsWith("/admin") ||
                location.pathname.startsWith("/about")
                  ? " active"
                  : "")
              }
              data-testid="profile-trigger"
              role="button"
              aria-controls="menu"
              id="menubutton"
              aria-haspopup="true"
              aria-label="Profile menu"
              aria-expanded={open ? "true" : "false"}
            >
              <PolyIcon
                icon="hamburger"
                className={`white main-icon ${open ? "active" : ""}`}
                title={t("OPEN_PROFILE")}
              />
            </span>
          )}
        />
        <ul
          className={dropdownClasses}
          aria-labelledby="menubutton"
          data-testid="profile-dropdown"
          id="menu"
        >
          <li
            className="border"
            onClick={() => {
              trackMenuSelection("User Profile");
              closeMenu();
            }}
          >
            <Link
              to="/admin/profile"
              data-testid={user.name + "-id"}
              className="user-name"
            >
              <PolyIcon icon="person_outline" className="white mr-5" />
              {user.name}
            </Link>
          </li>
          {hasUserListPermission && (
            <li
              className="border bottom"
              onClick={() => {
                trackMenuSelection("Poly Users");
                closeMenu();
              }}
            >
              <Link to="/admin/poly-users">
                <PolyIcon icon="poly_propeller" className="white mr-5" />
                {t("POLY_USERS")}
              </Link>
            </li>
          )}
          <FeatureFlagsOrEnabled
            feature="SITE_TOPOLOGY_IMPORT"
            orFeature="IS_POLY_USER"
          >
            <li
              onClick={() => {
                trackMenuSelection("Advanced Tools");
                closeMenu();
              }}
            >
              <Link to="/admin/tools">
                <PolyIcon icon="wrench" className="white mr-5" />
                {t("ADVANCED_TOOLS")}
              </Link>
            </li>
          </FeatureFlagsOrEnabled>
          <li onClick={closeMenu}>
            <Link
              to={
                hasClaritiTrialAdminPermission ? "/clariti" : "/clariti/trial"
              }
            >
              <PolyIcon icon="video" className="white mr-5" />
              {t(hasClaritiTrialAdminPermission ? "CLARITI" : "CLARITI_TRIAL")}
            </Link>
          </li>
          <li
            onClick={() => {
              trackMenuSelection("What's New");
              closeMenu();
            }}
          >
            <HelpLink location={DOCS.BLOG}>
              <PolyIcon icon="megaphone" className="white mr-5" />
              {t("HELP_NEW")}
            </HelpLink>
          </li>
          <li
            className="border bottom"
            onClick={() => {
              trackMenuSelection("About Poly Lens");
              closeMenu();
            }}
          >
            <Link to="/about">
              <PolyIcon icon="info_circle" className="white mr-5" />
              {t("ABOUT_LENS")}
            </Link>
          </li>
          <li
            onClick={() => {
              trackMenuSelection("logout");
              logout();
            }}
            className="button border bottom"
            data-testid="sign-out"
          >
            <PolyIcon icon="clear_circle_outline" className="white mr-5" />
            {t("SIGN_OUT")}
          </li>
          <li className="theme-switcher">
            <PolyIcon
              icon="sun"
              onClick={() => {
                setTheme(THEME_LIGHT as LensTheme);
              }}
            />
            <ToggleSwitchInput
              name="themeMode"
              checked={THEME_LIGHT !== theme}
              onChange={() => {
                let newTheme = theme === THEME_DARK ? THEME_LIGHT : THEME_DARK;
                setTheme(newTheme as LensTheme);
              }}
            />
            <PolyIcon
              icon="moon"
              onClick={() => {
                setTheme(THEME_DARK as LensTheme);
              }}
            />
          </li>
          <FeatureFlagEnabled feature="DEBUG_USER_ROLES">
            <li className="dev-switcher border top">
              <span className="mr-n5">{t("DEBUG_USER_ROLES")}</span>
              <ToggleSwitchInput
                name="debugMode"
                checked={debugUserRoles}
                onChange={() =>
                  dispatch(updateEnv({ debugUserRoles: !debugUserRoles }))
                }
              />
            </li>
          </FeatureFlagEnabled>
        </ul>
      </nav>
    </span>
  );
};

export default AdminMenu;
